const experienceData = {
  generalInfo: {
    teacherName: 'Хлопик Роман Зіновійович',
    position: 'Вчитель інформатики',
    school: 'Ліцей №2 Дрогобицької міської ради Львівської області',
    pedagogicalTheme: 'Формування цифрової компетентності та алгоритмічного мислення учнів засобами практико-орієнтованого програмування, STEM-проєктів та сучасних EdTech-інструментів',
    credo: 'Навчати — значить подвійно вчитися. Кожна дитина — це цілий світ, який чекає на відкриття, а програмування — універсальна мова для втілення найсміливіших дитячих ідей у реальність.',
    intro: `Мій педагогічний досвід ґрунтується на поєднанні фундаментальних академічних знань з інформатики та програмування із сучасними практичними вебтехнологіями. Головна мета моєї педагогічної діяльності — не просто навчити синтаксису мов програмування чи роботі з готовими програмами, а сформувати у вихованців гнучке алгоритмічне й критичне мислення, вміння проєктувати власні цифрові рішення та працювати в команді над суспільно важливими завданнями.`
  },

  principles: [
    {
      title: 'Практико-орієнтоване навчання',
      description: 'Кожна теоретична тема одразу закріплюється створенням корисного практичного продукту: консольної утиліти, гри на Python, вебсторінки чи чат-бота.',
      icon: 'Laptop'
    },
    {
      title: 'Розвиток алгоритмічного мислення',
      description: 'Навчання декомпозиції складних проблем, пошуку оптимальних алгоритмів, аналізу складності та логіки ухвалення рішень.',
      icon: 'Cpu'
    },
    {
      title: 'Індивідуалізація та наставництво',
      description: 'Побудова гнучкої траєкторії для кожного учня — від базового засвоєння програми до олімпіадного програмування високого рівня та наукових робіт МАН.',
      icon: 'UserCheck'
    },
    {
      title: 'STEM-інтеграція та проєктний підхід',
      description: 'Міждисциплінарний зв’язок інформатики з математикою, фізикою та лінгвістикою через реалізацію дослідницьких учнівських проєктів у товаристві «Еврика».',
      icon: 'Lightbulb'
    },
    {
      title: 'Використання інновацій та штучного інтелекту',
      description: 'Етичне й ефективне залучення інструментів генеративного ШІ (ChatGPT, Claude, Copilot) для аналізу коду, рефакторингу та швидкого прототипування.',
      icon: 'Bot'
    },
    {
      title: 'Формувальне оцінювання та рефлексія',
      description: 'Створення атмосфери довіри, де помилка в коді — це природна точка зростання, а code review розвиває культуру конструктивної критики.',
      icon: 'Target'
    }
  ],

  methods: [
    {
      id: 'pair-coding',
      title: 'Командне та парне програмування (Pair & Team Programming)',
      badge: 'Інтерактивна методика',
      color: 'from-blue-600 to-indigo-600',
      icon: 'Code2',
      description: 'Організація роботи учнів у парах «водій — штурман» або малих Agile-командах під час створення навчальних проєктів.',
      highlights: [
        'Розвиток soft skills: вміння аргументувати власні технічні рішення та слухати партнера',
        'Взаємний аналіз коду (Peer Code Review) та оперативний пошук логічних помилок',
        'Підвищення впевненості невпевнених учнів завдяки підтримці однокласників'
      ]
    },
    {
      id: 'gamification',
      title: 'Гейміфікація та навчальні хакатони',
      badge: 'Мотивація учнів',
      color: 'from-emerald-600 to-teal-600',
      icon: 'Gamepad2',
      description: 'Залучення ігрових механік (змагання, лідерборди, міні-хакатони на уроці) для підвищення мотивації до вивчення алгоритмів.',
      highlights: [
        'Використання інтерактивних платформ алгоритмізації та платформ автоперевірки коду',
        'Шкільні міні-хакатони з розробки тематичних вебквестів та навчальних симуляторів',
        'Нагороди за найчистіший код, нестандартний підхід та оптимізацію алгоритмів'
      ]
    },
    {
      id: 'research-projects',
      title: 'Дослідницько-пошукова діяльність (МАН і «Еврика»)',
      badge: 'Обдарована молодь',
      color: 'from-amber-600 to-orange-600',
      icon: 'Compass',
      description: 'Система супроводу талановитих учнів у створенні повноцінних програмних систем та наукових досліджень.',
      highlights: [
        'Повний життєвий цикл розробки: від формулювання гіпотези до написання коду й захисту',
        'Підготовка інтелектуальних систем для аналізу даних та локальних цифрових сервісів',
        'Щорічні призові місця на захистах наукових робіт учнів-членів МАН України'
      ]
    },
    {
      id: 'digital-ecosystem',
      title: 'Власна цифрова екосистема та відкриті ресурси',
      badge: 'Авторський інструментарій',
      color: 'from-purple-600 to-pink-600',
      icon: 'Globe',
      description: 'Впровадження авторських цифрових розробок («Дрогобич Талант Хаб», анкети самооцінювання, інтерактивні довідники) у щоденну практику.',
      highlights: [
        'Автоматизація збору зворотного зв’язку та аналізу навчальних досягнень',
        'Відкритий доступ до інтерактивних матеріалів для колег-педагогів та учнів',
        'Постійне оновлення контенту згідно з вимогами Професійного стандарту вчителя'
      ]
    }
  ],

  systemStages: [
    {
      step: '01',
      title: 'Діагностика та мотивація',
      subtitle: 'Виявлення інтересів і базового рівня',
      text: 'Вхідне тестування, інтерактивні анкети зацікавлень, знайомство зі світом сучасних IT-професій та формування мотивації до творчого мислення.'
    },
    {
      step: '02',
      title: 'Базовий алгоритмічний фундамент',
      subtitle: 'Синтаксис, логіка, структури даних',
      text: 'Опанування основ програмування (Python, основи вебтехнологій), розв’язування прикладних задач та формування культури чистого коду.'
    },
    {
      step: '03',
      title: 'Проєктний практикум',
      subtitle: 'Реалізація прикладних задумів',
      text: 'Командна та індивідуальна робота над авторськими проєктами: від ідеї до працюючого прототипу, робота з API та інтерфейсами.'
    },
    {
      step: '04',
      title: 'Олімпіадний та науковий рівень',
      subtitle: 'МАН, олімпіади та профорієнтація',
      text: 'Поглиблена індивідуальна підготовка, алгоритми підвищеної складності, участь у конкурсах та профорієнтаційна співпраця з IT-спільнотою.'
    }
  ],

  results: [
    {
      metric: '18+',
      label: 'Переможців та призерів олімпіад з інформатики та ІТ',
      subtext: 'на рівні територіальної громади, району та області'
    },
    {
      metric: '100%',
      label: 'Охоплення проєктною діяльністю',
      subtext: 'кожен учень створює свій підсумковий цифровий проєкт'
    },
    {
      metric: '3+',
      label: 'Авторські цифрові освітні платформи',
      subtext: 'впроваджені в освітній процес міста та регіону'
    },
    {
      metric: '20+',
      label: 'Років педагогічного досвіду',
      subtext: 'безперервного фахового зростання та впровадження інновацій'
    }
  ]
};

export default experienceData;
